import { Fragment } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import StatusBadge from '@/components/status-badge/status-badge';
import { OutboundCall } from '@/services/outbound-calls-service/calls.service.types';
import {
  TRANSFER_RESULT_BADGE,
  conclusionParts,
  dispositionLabel,
  extractedDataRows,
  objectiveLabel,
  transferResultLabel,
} from './outbound-calls.utils';
import styles from './outbound-calls.module.scss';

interface CallOutcomeProps {
  disposition: string | null;
  objectiveCompleted: boolean | null;
  live?: boolean;
}

/** How the call went: on the air, or its disposition and, once judged, the objective. */
export function CallOutcome({ disposition, objectiveCompleted, live }: CallOutcomeProps) {
  const { t } = useTranslation('outbound');

  if (live) return <StatusBadge variant="live" label={t('call.live')} />;

  const objective = objectiveLabel(objectiveCompleted);
  return (
    <span className={styles.stack}>
      <span>{dispositionLabel(disposition)}</span>
      {objective && <StatusBadge variant={objectiveCompleted ? 'ok' : 'idle'} label={objective} />}
    </span>
  );
}

/** The reason code as a label, then the agent's own words. */
export function Conclusion({ conclusion, clamp }: { conclusion: string | null; clamp?: boolean }) {
  const parts = conclusionParts(conclusion);
  if (!parts) return null;

  return (
    <span className={styles.stack}>
      {parts.label && <b>{parts.label}</b>}
      {parts.detail && (
        <span className={clsx(styles.secondary, clamp && styles.clamp)}>{parts.detail}</span>
      )}
    </span>
  );
}

/** The hand-over to a salesperson: nothing until one was attempted. */
export function TransferOutcome({
  call,
}: {
  call: Pick<OutboundCall, 'transfer_result' | 'transferred_to'>;
}) {
  if (!call.transfer_result) return null;

  return (
    <span className={styles.stack}>
      <StatusBadge
        variant={TRANSFER_RESULT_BADGE[call.transfer_result] ?? 'idle'}
        label={transferResultLabel(call.transfer_result)}
      />
      {call.transferred_to && <span className={styles.secondary}>{call.transferred_to.name}</span>}
    </span>
  );
}

/** What the agent concluded and collected, and the way to the recording and transcript. */
export function CallResult({ call }: { call: OutboundCall }) {
  const { t } = useTranslation('outbound');
  const data = extractedDataRows(call.extracted_data);

  return (
    <>
      {call.conclusion && (
        <div className={styles.stack}>
          <span className={styles.label}>{t('call.conclusion')}</span>
          <Conclusion conclusion={call.conclusion} />
        </div>
      )}
      {data.length > 0 && (
        <div className={styles.stack}>
          <span className={styles.label}>{t('call.extractedData')}</span>
          <dl className={styles.data} aria-label={t('call.extractedData')}>
            {data.map(({ key, value }) => (
              <Fragment key={key}>
                <dt>{key}</dt>
                <dd>{value}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      )}
      {/* The conversation drawer already plays the recording and shows the transcript. */}
      <Link to={`/conversations/${call.id}`}>{t('call.callDetails')}</Link>
    </>
  );
}
